import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingOverview } from '@/types/leveling'
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PostLayeredView, Select } from '@maxroll-media-group/common-libs'
import { Widget } from 'leveling/widget'
import React from 'react'
import './strategies.scss'

const strategyCategoryToShortLabel: Record<string, string> = {
  undefined: 'Guide',
  'starter-strategies': 'Starter',
  'endgame-strategies': 'Endgame',
  'farming-guides': 'Guide'
}

const strategyCategoryToTag: Record<
  string,
  { children: string; tagColor: string }
> = {
  undefined: { children: 'Farming Guide', tagColor: 'purple' },
  'starter-strategies': { children: 'Starter Guide', tagColor: 'green' },
  'endgame-strategies': { children: 'Endgame Guide', tagColor: 'purple' },
  'farming-guides': { children: 'Farming Guide', tagColor: 'purple' }
}

type LevelingStrategy = Exclude<LevelingOverview['strategies'], undefined>[0]

export function Strategies({
  embed,
  onEmbedChange
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
}) {
  const [currencyStrategies, setCurrencyStrategies] = React.useState<any[]>([])

  React.useEffect(() => {
    const url = 'https://meilisearch-proxy.maxroll.gg/indexes/wp_posts_6/search'
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization:
          'Bearer 35679298edc476d0b9f9638cdb90d362235a62550bea39d59544f694cc9d90b9'
      },
      body: JSON.stringify({
        filter: [
          '(taxonomies.category = "starter-strategies") OR (taxonomies.category = "endgame-strategies") OR (taxonomies.category = "farming-guides")'
        ],
        limit: 100
      })
    })
      .then((response) => response.json())
      .then((result) => {
        const strategies = []

        for (const hit of result.hits) {
          strategies.push({
            strategyCategory:
              strategyCategoryToShortLabel[
                hit.taxonomies?.category?.[1] ?? 'undefined'
              ],
            strategyCategoryTag:
              strategyCategoryToTag[
                hit.taxonomies?.category?.[1] ?? 'undefined'
              ],
            ...hit
          })
        }

        setCurrencyStrategies([
          ...strategies.filter(
            (strategy) => strategy.strategyCategory === 'Starter'
          ),
          ...strategies.filter(
            (strategy) => strategy.strategyCategory === 'Endgame'
          ),
          ...strategies.filter(
            (strategy) => strategy.strategyCategory === 'Guide'
          )
        ])
      })
      .catch(() => {
        // Do nothing
      })
  }, [])

  const content = embed?.overview?.strategies || []
  const [isEditing, setIsEditing] = React.useState(false)

  const onAdd = React.useCallback(
    () =>
      onEmbedChange?.((embed) => {
        if (embed.type !== 'leveling') return embed
        embed = { ...embed }
        embed.overview = { ...embed.overview }
        embed.overview.strategies = embed.overview.strategies?.slice() ?? []
        embed.overview.strategies.push({ id: '' })
        return embed
      }),
    [onEmbedChange]
  )

  const onDelete = (index: number) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.overview = { ...embed.overview }
      embed.overview.strategies = embed.overview.strategies?.slice() ?? []
      embed.overview.strategies.splice(index, 1)
      return embed
    })

  const onEditAttr = <K extends keyof LevelingStrategy>(
    index: number,
    attr: K,
    newValue: LevelingStrategy[K]
  ) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.overview = { ...embed.overview }
      embed.overview.strategies = embed.overview.strategies?.slice() ?? []
      embed.overview.strategies[index] = {
        ...embed.overview.strategies[index],
        [attr]: newValue
      }
      return embed
    })

  const strategyOptions = React.useMemo(() => {
    return currencyStrategies.map((strategy) => ({
      value: strategy.id as string,
      label: (
        <>
          <strong>{strategy.strategyCategory}</strong> {strategy.post_title}
        </>
      ) as any as string
    }))
  }, [currencyStrategies])

  return (
    <Widget
      title={
        <>
          Recommended Strategies
          {onEmbedChange &&
            (isEditing ? (
              <div>
                <button onClick={() => setIsEditing(false)}>Close</button>
              </div>
            ) : (
              <div>
                <FontAwesomeIcon
                  icon={faEdit}
                  onClick={() => {
                    setIsEditing(!isEditing)
                  }}
                />
              </div>
            ))}
        </>
      }>
      {(Array.isArray(content) && content.length > 0) || onEmbedChange ? (
        <div className="poe-strategies">
          {isEditing ? (
            <div className="poe-strategies-edit">
              {content.map((item, index) => (
                <div key={index}>
                  <Select
                    className="poe-commons-lib-select"
                    options={strategyOptions}
                    required
                    placeholder="Strategy"
                    value={item.id}
                    onChange={(newValue) => {
                      if (newValue) onEditAttr(index, 'id', newValue.value)
                    }}
                  />
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    onClick={() => {
                      onDelete(index)
                    }}
                  />
                </div>
              ))}
              <button onClick={onAdd}>Add</button>
            </div>
          ) : (
            content.map((item, index) => {
              const hit = currencyStrategies.find(
                (strategy) => strategy.id === item.id
              )
              return hit ? (
                <PostLayeredView
                  key={index}
                  post={hit}
                  openInNewTab={true}
                  postStyle="left-faded-to-black"
                  tag={hit.strategyCategoryTag}
                />
              ) : null
            })
          )}
        </div>
      ) : null}
    </Widget>
  )
}
